import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { confirmDelete } from '../utils/confirmDelete';
import { T, PageHeader, Alert } from '../components/UI';

export default function Dealerships() {
  const navigate = useNavigate();
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [error,    setError]    = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing,  setEditing]  = useState(null); // {id, name, ...}
  const [form,     setForm]     = useState({ name:'', contactName:'', contactEmail:'', contactPhone:'', address:'' });
  const [saving,   setSaving]   = useState(false);
  const [deleting, setDeleting] = useState(null);

  const fetch = async () => {
    setLoading(true);
    try { const { data } = await api.get('/jobs/dealerships'); setItems(data.dealerships); }
    catch { setError('Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true); setError(null);
    try {
      if (editing) {
        await api.patch(`/jobs/dealerships/${editing._id}`, form);
      } else {
        await api.post('/jobs/dealerships', form);
      }
      setForm({ name:'', contactName:'', contactEmail:'', contactPhone:'', address:'' });
      setShowForm(false); setEditing(null);
      fetch();
    } catch (err) { setError(err.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const startEdit = (item) => {
    setEditing(item);
    setForm({ name:item.name||'', contactName:item.contactName||'', contactEmail:item.contactEmail||'', contactPhone:item.contactPhone||'', address:item.address||'' });
    setShowForm(true);
    window.scrollTo({top:0, behavior:'smooth'});
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Remove "${name}"? This won't delete existing jobs.`)) return;
    if (!await confirmDelete()) return;
    setDeleting(id);
    try { await api.delete(`/jobs/dealerships/${id}`); fetch(); }
    catch { setError('Failed to remove'); }
    finally { setDeleting(null); }
  };

  const cancelForm = () => { setShowForm(false); setEditing(null); setForm({ name:'', contactName:'', contactEmail:'', contactPhone:'', address:'' }); };

  return (
    <div style={T.page}>
      <PageHeader title="Dealerships" onBack={()=>navigate(-1)}
        actions={
          <button onClick={()=>{ cancelForm(); setShowForm(s=>!s); }} style={T.btnPrimary}>
            <i className="ti ti-plus" aria-hidden="true"/> Add
          </button>
        }
      />
      <div style={{ maxWidth:'700px', margin:'0 auto', padding:'16px 16px 80px' }}>
        {error && <Alert type="error">{error}</Alert>}

        {showForm && (
          <div style={{ background:'var(--surface)', border:`1px solid ${editing?'var(--amber)':'var(--blue)'}`, borderRadius:'var(--radius-lg)', padding:'16px', marginBottom:'16px' }}>
            <div style={{ fontSize:'13px', fontWeight:'500', color: editing?'var(--amber)':'var(--blue)', marginBottom:'14px' }}>
              {editing ? `Edit: ${editing.name}` : 'Add New Dealership'}
            </div>
            <form onSubmit={handleSubmit}>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(200px,1fr))', gap:'10px', marginBottom:'10px' }}>
                {[
                  {key:'name',         label:'Dealership Name *', placeholder:'e.g. Hyundai Osborne Park', req:true},
                  {key:'contactName',  label:'Contact Name',       placeholder:'Fleet Manager'},
                  {key:'contactEmail', label:'Contact Email',      placeholder:'fleet@dealer.com'},
                  {key:'contactPhone', label:'Contact Phone',      placeholder:'08 9XXX XXXX'},
                ].map(f=>(
                  <div key={f.key}>
                    <label style={T.label}>{f.label}</label>
                    <input value={form[f.key]} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))}
                      style={T.input} placeholder={f.placeholder} required={f.req}
                      onFocus={e=>e.target.style.borderColor=editing?'var(--amber)':'var(--blue)'}
                      onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
                  </div>
                ))}
              </div>
              <div style={{ marginBottom:'12px' }}>
                <label style={T.label}>Address</label>
                <input value={form.address} onChange={e=>setForm(p=>({...p,address:e.target.value}))}
                  style={T.input} placeholder="123 Main St, Perth WA"
                  onFocus={e=>e.target.style.borderColor='var(--blue)'}
                  onBlur={e=>e.target.style.borderColor='var(--border-md)'}/>
              </div>
              <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
                <button type="button" onClick={cancelForm} style={T.btnSecondary}>Cancel</button>
                <button type="submit" disabled={saving} style={{ ...T.btnPrimary, background:editing?'var(--amber)':'var(--blue)', opacity:saving?0.6:1 }}>
                  {saving ? 'Saving...' : editing ? 'Save Changes' : 'Add Dealership'}
                </button>
              </div>
            </form>
          </div>
        )}

        {loading ? <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)' }}>Loading...</div> : (
          <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
            {items.map(d=>(
              <div key={d._id} style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)', padding:'14px 16px', display:'flex', alignItems:'center', gap:'12px' }}>
                <div style={{ width:'38px', height:'38px', background:'var(--blue-light)', borderRadius:'var(--radius)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <i className="ti ti-building" style={{ color:'var(--blue)', fontSize:'18px' }} aria-hidden="true"/>
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontWeight:'500', color:'var(--text)', fontSize:'14px' }}>{d.name}</div>
                  <div style={{ fontSize:'12px', color:'var(--muted)', marginTop:'2px', display:'flex', gap:'12px', flexWrap:'wrap' }}>
                    {d.contactName  && <span>{d.contactName}</span>}
                    {d.contactEmail && <span>{d.contactEmail}</span>}
                    {d.contactPhone && <span>{d.contactPhone}</span>}
                    {d.address      && <span>{d.address}</span>}
                  </div>
                </div>
                <div style={{ display:'flex', gap:'6px', flexShrink:0 }}>
                  <button onClick={()=>startEdit(d)} style={{ ...T.btnIcon, color:'var(--blue)', borderColor:'rgba(10,110,180,0.25)' }}>
                    <i className="ti ti-edit" style={{ fontSize:'14px' }} aria-label="Edit"/>
                  </button>
                  <button onClick={()=>handleDelete(d._id, d.name)} disabled={deleting===d._id} style={{ ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)' }}>
                    <i className="ti ti-trash" style={{ fontSize:'14px' }} aria-label="Delete"/>
                  </button>
                </div>
              </div>
            ))}
            {items.length === 0 && (
              <div style={{ textAlign:'center', padding:'40px', color:'var(--muted)', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-lg)' }}>
                No dealerships yet. Click Add to get started.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
